/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { QuickMenuShortcutV4 } from "../../model/types";
export type {
  QuickMenuShortcutV4,
  UiDestinationTypeV4
} from "../../model/types";
import type { HomeScreenDimmerState } from "./HomeScreenDimmerState";
import {
  TCarouselRailPaginationState,
  TTilePaginationState
} from "../utility/carouselPagination";

// ─── Pagination Types (re-exported from shared carouselPagination) ───
export type {
  TCarouselRailPaginationState,
  TCarouselRailPage,
  TTilePaginationState,
  TTilePage
} from "../utility/carouselPagination";

// ─── HomeScreen Types ───
/** Identifies which screen element currently holds D-pad / pointer focus. */
export enum HomescreenFocusedField {
  MENU_STRIP = "menuStrip",
  PROFILE_ICON = "profileIcon",
  CAROUSELS = "carousels",
  NOW_PLAYING_TILE = "nowPlayingTile"
}

/**
 * Unified Redux state for the HomeScreen feature.
 * Contains both carousel content and quick-menu shortcut data.
 */
export interface HomescreenState {
  // ── Carousel rail pagination (cursor-based, NAV_DOWN-driven) ──
  /** Cursor-paginated rails fetched via screenGet. */
  carouselsPagination: TCarouselRailPaginationState;

  // ── Per-rail tile pagination (offset-based, NAV_RIGHT-driven) ──
  /**
   * Offset-paginated tiles per carousel, keyed by rail id.
   * Seeded from the initial screenGet response and extended via carouselGet.
   */
  tilePagination: Record<string, TTilePaginationState>;

  // ── Carousel focus ──
  /** Index of the currently focused carousel rail. */
  focusedCarouselIndex: number;
  /**
   * Map of carouselIndex → last focused tile index.
   * Persists each carousel's position independently so navigation
   * between carousels restores the previously highlighted tile.
   */
  focusedTileIndices: Partial<Record<number, number>>;

  // ── Refresh state ──
  /**
   * Server-provided TTL in seconds until the next carousel refresh.
   * Default: 120 — overwritten by the first API response.
   */
  refreshIntervalSecs: number;
  /**
   * Date.now() timestamp of the last successful carousel fetch.
   * Used by the TTL guard to skip redundant refreshes on foreground restore
   * and network reconnect when data is still fresh.
   * Default: 0 — forces the first on-resume to always re-fetch.
   */
  lastFetchTimestamp: number;
  /**
   * The profile the carousels were fetched for. Carousels are per profile, so
   * a switch makes them stale even inside the TTL — the TTL guard compares
   * this against the active profile.
   * Default: "" — no fetch made yet.
   */
  carouselsProfileId: string;

  // ── Quick-menu shortcut state ──
  /** Whether quick-menu shortcuts are being fetched. */
  isLoadingShortcuts: boolean;
  /** Whether quick-menu shortcuts have been loaded (from API, cache, or defaults). */
  isShortcutsLoaded: boolean;
  /** Error message if shortcut fetch failed. */
  shortcutsError: string;
  /** Quick-menu shortcut items to render in the menu strip. */
  quickMenuShortcuts: QuickMenuShortcutV4[];
  /** Cached NPVR gate; when false, My Hub is hidden from the visible menu. */
  isNpvrRecordingAllowed?: boolean;
  /** Index of the currently focused menu strip item. */
  focusedShortcutIndex: number;

  // ── Focus routing ──
  /** Which screen element currently holds D-pad focus. */
  focusedField: HomescreenFocusedField;

  // ── Dimmer state ──
  /** Current visual dimmer phase driven by focus position. */
  dimmerState: HomeScreenDimmerState;
}
